import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { BulkActionToolbar } from '@ooh/ui/components/bulk-action-toolbar';
import type { BulkAction } from '@ooh/ui/components/bulk-action-toolbar';
import { RemoveUnitsConfirmDialog } from './remove-units-confirm-dialog';
import type { TFunction } from 'i18next';

interface BulkActionsOverlayProps {
  t: TFunction<'offers'>;
  selectedCount: number;
  /**
   * Number of the selected units that carry a reservation held by this
   * offer — `0` until task 4.2 wires the per-row availability data the real
   * count is computed from.
   */
  reservedCount?: number;
  /** Of `selectedCount`, how many are not in the current results (a filter hides them). */
  hiddenCount?: number;
  removeConfirmOpen: boolean;
  onClearSelection: () => void;
  onOpenRemoveConfirm: () => void;
  onCloseRemoveConfirm: () => void;
  onConfirmRemove: () => void;
}

export function BulkActionsOverlay({
  t,
  selectedCount,
  reservedCount = 0,
  hiddenCount = 0,
  removeConfirmOpen,
  onClearSelection,
  onOpenRemoveConfirm,
  onCloseRemoveConfirm,
  onConfirmRemove,
}: BulkActionsOverlayProps) {
  const actions: BulkAction[] = [
    {
      id: 'remove-selected',
      label: t('workspace.unitsTab.bulkActions.removeSelected'),
      icon: <Trash2 />,
      variant: 'destructive',
      onClick: onOpenRemoveConfirm,
    },
  ];

  return (
    <>
      <BulkActionToolbar
        selectedCount={selectedCount}
        visibleCount={selectedCount - hiddenCount}
        visibleLabel={(count) => t('workspace.unitsTab.bulkActions.visibleCount', { count })}
        aria-label={t('workspace.unitsTab.bulkActions.ariaLabel')}
        selectedLabel={(count) => t('workspace.unitsTab.bulkActions.selectedCount', { count })}
        moreLabel={t('workspace.unitsTab.bulkActions.moreActions')}
        clearLabel={t('workspace.unitsTab.bulkActions.clearSelection')}
        onClearSelection={onClearSelection}
        actions={actions}
      />

      {removeConfirmOpen ? (
        <RemoveUnitsConfirmDialog
          title={t('workspace.unitsTab.removeConfirm.title')}
          description={t('workspace.unitsTab.removeConfirm.description')}
          reservedCount={reservedCount}
          reservationNotice={
            reservedCount > 0
              ? t('workspace.unitsTab.removeConfirm.reservationNotice', {
                  reserved: reservedCount,
                  total: selectedCount,
                })
              : undefined
          }
          hiddenCount={hiddenCount}
          hiddenNotice={
            hiddenCount > 0
              ? t('workspace.unitsTab.removeConfirm.hiddenNotice', { count: hiddenCount })
              : undefined
          }
          cancelLabel={t('workspace.unitsTab.removeConfirm.cancel')}
          confirmLabel={t('workspace.unitsTab.removeConfirm.confirm')}
          onCancel={onCloseRemoveConfirm}
          onConfirm={onConfirmRemove}
        />
      ) : null}
    </>
  );
}
